import React from "react"

import { useTranslation } from "react-i18next"

import type { DateChangeHandler } from "@/base/types"
import { DatePickerWeb } from "@/components/DatepickerWeb"
import type { AcceptsChildren, RowProps } from "@/theme"
import { AppText, Box, Row, Z_INDEX } from "@/theme"

export interface MinDateMaxDatePickerProps {
  isDisabled?: boolean
  maxDateMs: number
  minDateMs: number
  onChangeMaxDate: DateChangeHandler
  onChangeMinDate: DateChangeHandler
  /**
   * Optional commit callbacks fired when the calendar closes (selection,
   * Enter, or blur). Use these to defer expensive work (e.g. a data refetch)
   * until the user has finished editing, rather than running it on every
   * keystroke handled by onChange*.
   */
  onCommitMaxDate?: () => void
  onCommitMinDate?: () => void
}

function PickerWrapper({
  children,
  title,
  zIndex,
}: AcceptsChildren & { title: string; zIndex: number }): React.JSX.Element {
  return (
    <Box zIndex={zIndex}>
      <AppText colorScheme={"secondary"} fontSize={"$xs"}>
        {title}
      </AppText>
      {children}
    </Box>
  )
}
/**
 * Standardized datepicker for min/max date (day)
 */
export function MinDateMaxDatePickers({
  isDisabled = false,
  maxDateMs,
  minDateMs,
  onChangeMaxDate,
  onChangeMinDate,
  onCommitMaxDate,
  onCommitMinDate,
  zIndex,
  ...rest
}: MinDateMaxDatePickerProps & RowProps): React.JSX.Element {
  const { t } = useTranslation()
  const dateMin = new Date(minDateMs)
  const dateMax = new Date(maxDateMs)
  if (typeof zIndex === "undefined") {
    zIndex = Z_INDEX[1]
  }
  return (
    <Row space={"$1"} style={{ zIndex }} {...rest}>
      <PickerWrapper title={t("datepickersMinDateTitle")} zIndex={zIndex}>
        <DatePickerWeb
          shouldCloseOnSelect
          calendarClassName={"datepicker-calendar"}
          className={"react-datepicker"}
          disabled={isDisabled}
          endDate={dateMin}
          maxDate={dateMax}
          popperPlacement={"left-start"}
          portalId={"datepicker-portal"}
          selected={dateMin}
          onCalendarClose={onCommitMinDate}
          onChange={onChangeMinDate}
        />
      </PickerWrapper>
      <PickerWrapper title={t("datepickersMaxDateTitle")} zIndex={zIndex}>
        <DatePickerWeb
          shouldCloseOnSelect
          calendarClassName={"datepicker-calendar"}
          className={"react-datepicker"}
          disabled={isDisabled}
          maxDate={new Date()}
          popperPlacement={"left-start"}
          portalId={"datepicker-portal"}
          selected={dateMax}
          startDate={dateMax}
          onCalendarClose={onCommitMaxDate}
          onChange={onChangeMaxDate}
        />
      </PickerWrapper>
    </Row>
  )
}
